import type { RuleSet } from '../rules';
import { type Building, isCity, type PlayerId, type SimState } from '../sim';
import { type Names, nameOf } from './names';
import { orderedResources, resourceIcon } from './resources';

/** The player's completed cities (numbered for display by `numberCities`). */
export function ownCities(state: SimState, rules: RuleSet, playerId: PlayerId): Building[] {
  return Object.values(state.buildings).filter(
    (b) => b.owner === playerId && isCity(b, rules) && b.buildProgress >= 1
  );
}

/**
 * Stable city numbers (City 1, City 2, …): each city (pass sites too, so
 * numbers follow founding order) gets the next number the first time it's
 * seen, in founding (id) order, and keeps it, so
 * losing City 2 doesn't renumber City 3. `numbers` is updated in place and
 * returned; numbers of fallen cities aren't reused.
 */
export function numberCities(numbers: Map<string, number>, cities: readonly Building[]) {
  const fresh = cities
    .filter((c) => !numbers.has(c.id))
    .sort((a, b) => Number.parseInt(a.id.slice(1), 10) - Number.parseInt(b.id.slice(1), 10));
  let next = Math.max(0, ...numbers.values()) + 1;
  for (const c of fresh) numbers.set(c.id, next++);
  return numbers;
}

/**
 * "🌾 12 🪵 3": a city's stock (whole units: `unitAmounts` of the state's
 * thousandths), rounded down like the HUD totals; food and wood always,
 * anything else (metal) once there's a whole unit of it.
 */
export function stockLabel(stock: Readonly<Record<string, number>> | undefined): string {
  const held: Record<string, number> = { food: 0, wood: 0, ...stock };
  return orderedResources(held)
    .filter((res) => res === 'food' || res === 'wood' || Math.floor(held[res] ?? 0) > 0)
    .map((res) => `${resourceIcon(res)} ${Math.floor(held[res] ?? 0)}`)
    .join(' ');
}

/**
 * How a city reads in hints: "City 2" for the player's own (by its stable
 * number), "Jolly Swagman's city" for a rival's (`playerName`, else the id).
 * Pass only cities the player knows of. An own city the player named
 * (`names`, client-local) reads by that name instead.
 */
export function cityName(
  city: Building,
  playerId: PlayerId,
  numbers: ReadonlyMap<string, number>,
  playerName: (id: PlayerId) => string = (id) => id,
  names?: Names
): string {
  if (city.owner !== playerId) return `${playerName(city.owner)}'s city`;
  const given = nameOf(names, city.id);
  if (given) return given;
  const n = numbers.get(city.id);
  return n === undefined ? 'your city' : `City ${n}`;
}

/** A city chip still shown, struck through, after its city fell. */
export interface LostCity {
  id: string;
  number: number;
  /** Sim time it was noticed gone. */
  since: number;
}

/** Seconds a fallen city's chip stays in the Cities row, marked lost. */
export const LOST_CHIP_SECONDS = 5;

/**
 * Updates the lost-city chips: a city in `prevIds` (last tick's completed
 * cities) missing from `currentIds` is added at `now`; entries older than
 * LOST_CHIP_SECONDS (or back, which can't happen today) drop off.
 */
export function updateLostCities(
  lost: readonly LostCity[],
  prevIds: readonly string[],
  currentIds: ReadonlySet<string>,
  numbers: ReadonlyMap<string, number>,
  now: number
): LostCity[] {
  const kept = lost.filter((l) => now - l.since <= LOST_CHIP_SECONDS && !currentIds.has(l.id));
  for (const id of prevIds) {
    if (currentIds.has(id) || kept.some((l) => l.id === id)) continue;
    const number = numbers.get(id);
    if (number !== undefined) kept.push({ id, number, since: now });
  }
  return kept.length === lost.length && kept.every((l, i) => l === lost[i])
    ? (lost as LostCity[])
    : kept;
}
